<!-- ═══ Users & workspaces. The page's look — theme tokens, cards, switches, motion — lives in
     users-workspaces.component.css and is shared by the four components rendered inside this root.
     `uw-dark` / `uw-light` is the only thing the theme toggle changes. ═══ -->
<div class="uw h-full flex flex-col overflow-hidden relative"
     [ngClass]="darkMode ? 'uw-dark theme-dark' : 'uw-light theme-light'">

  <!-- Depth behind the header — nothing here is read, so nothing here is announced. -->
  <div class="uw-atmosphere" aria-hidden="true"></div>

  <!-- ═══ HEADER — aligned with the left-nav badge: the same bar Pages and Team open with ═══ -->
  <div class="shrink-0 pt-3 px-3 relative z-10">
    <!-- Three columns: the title takes what is left and truncates first, the tabs keep their own width
         and stay centred, and the search narrows below `lg` — so on a narrow window nothing slides
         under anything else. -->
    <div class="uw-headerbar h-14 pl-[60px] pr-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 rounded-xl">

      <div class="min-w-0">
        <h1 class="text-sm font-semibold text-(--uw-strong) truncate">Users &amp; workspaces</h1>
        <p class="text-[11px] text-(--uw-muted) truncate">The people who use your app, the workspaces they are in, and the way in.</p>
      </div>

      <!-- The two tabs, centred — shown only once we know this builder may see them. -->
      <div class="flex justify-center">
        <div *ngIf="!loadingAccess() && access() !== 'viewer'" class="uw-seg" role="tablist" aria-label="Users and workspaces">
          <button type="button" role="tab" (click)="tab.set('workspaces')" [attr.aria-selected]="tab() === 'workspaces'">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3.5 h-3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4M10 10h4M10 14h4M10 18h4"/></svg>
            Workspaces
          </button>
          <button type="button" role="tab" (click)="tab.set('roles')" [attr.aria-selected]="tab() === 'roles'">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3.5 h-3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Roles
          </button>
        </div>
      </div>

      <div class="flex items-center justify-end gap-1.5 min-w-0">
        <!-- One search box, on the Workspaces tab only — Roles has nothing to search. `/` focuses it
             from anywhere on the tab, Escape empties it (onDocumentKeydown / onSearchKeydown). -->
        <div *ngIf="!loadingAccess() && access() !== 'viewer' && tab() === 'workspaces'" class="relative group">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"
               class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--uw-faint) pointer-events-none"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input #searchBox type="text" [ngModel]="searchInput" (ngModelChange)="onSearchInput($event)" (keydown)="onSearchKeydown($event)"
                 placeholder="Search people and workspaces…" aria-label="Search people and workspaces"
                 autocomplete="off" spellcheck="false"
                 class="uw-field h-9 w-44 lg:w-72 lg:focus:w-80 pl-8 pr-9 text-[12.5px]" />
          <button *ngIf="searchInput" type="button" (click)="clearSearch(); searchBox.focus()" aria-label="Clear search" title="Clear search"
                  class="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md flex items-center justify-center text-(--uw-faint) hover:text-(--uw-strong) hover:bg-(--uw-hover) cursor-pointer transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="w-3 h-3"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
          <span *ngIf="!searchInput" class="uw-kbd absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">/</span>
          <!-- What the box matches — said while the box is in use, instead of permanently under it. -->
          <p class="absolute right-0 top-full mt-2 w-72 px-3 py-2 rounded-lg text-[11px] leading-relaxed text-(--uw-muted) bg-(--uw-raised) border border-(--uw-line) shadow-lg z-20
                    opacity-0 -translate-y-1 pointer-events-none transition-all duration-150 group-focus-within:opacity-100 group-focus-within:translate-y-0">
            Matches a name, an email, a username or a workspace name.
          </p>
        </div>

        <!-- Theme — the same switch as Pages', and it flips Pages and Team with it. -->
        <button type="button" (click)="darkMode = !darkMode"
                [attr.aria-label]="darkMode ? 'Switch to light theme' : 'Switch to dark theme'"
                [attr.title]="darkMode ? 'Light theme' : 'Dark theme'"
                class="uw-btn uw-btn-ghost h-9 w-9">
          <svg *ngIf="darkMode" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
          <svg *ngIf="!darkMode" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>
        </button>
      </div>
    </div>
  </div>

  <!-- ═══ BODY ═══ -->
  <div class="flex-1 overflow-y-auto overflow-x-hidden uw-scroll relative">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 pt-5 pb-20">

      <!-- Access is not known yet: a skeleton, never a spinner and never an empty page. Shaped like
           the Workspaces tab's own first-load skeleton, so one hands over to the other without a jump. -->
      <div *ngIf="loadingAccess()" class="space-y-6" aria-hidden="true">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div *ngFor="let i of [1,2,3]" class="uw-skel h-[70px]"></div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div *ngFor="let i of [1,2]" class="uw-skel h-[124px]"></div>
        </div>
        <div class="space-y-2.5">
          <div class="uw-skel h-[52px]"></div>
          <div *ngFor="let i of [1,2,3]" class="uw-skel h-[66px]"></div>
        </div>
      </div>

      <ng-container *ngIf="!loadingAccess()">
        <div *ngIf="access() === 'viewer'" class="uw-card uw-rise max-w-md mx-auto mt-12 px-8 py-10 flex flex-col items-center text-center">
          <div class="uw-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
          </div>
          <p class="text-sm font-semibold text-(--uw-strong)">This page is for owners and editors.</p>
          <p class="text-xs text-(--uw-muted) mt-1.5 leading-relaxed">Ask an owner of this project if you need to manage its users.</p>
        </div>

        <ng-container *ngIf="access() !== 'viewer'">
          <app-workspaces-tab *ngIf="tab() === 'workspaces'" [access]="access()" [search]="search()"
                              (clearSearch)="clearSearch()" />
          <app-roles-tab *ngIf="tab() === 'roles'" [access]="access()" />
        </ng-container>
      </ng-container>
    </div>
  </div>
</div>

<app-people-confirm></app-people-confirm>
